// Copyright 2022, Imprivata, Inc.  All rights reserved.

import type { NavigateFunction, NavigateOptions } from 'react-router';
import { generateWorkflowId, getWorkflowIdFromUrl } from './WorkflowIdHelper';

/**
 * Navigates to a path while preserving the Impr-Workflow-Id and Impr-Tenant-Id query parameters if they exist.
 * This ensures these IDs are maintained across page navigations and refreshes.
 *
 * @param navigate - The navigate function from react-router's useNavigate hook
 * @param path - The path to navigate to
 * @param options - Optional navigation options (state, replace, etc.)
 *
 * @example
 * ```tsx
 * const navigate = useNavigate();
 * navigateWithContext(navigate, '/verify');
 * // If Impr-Workflow-Id=abc123 and Impr-Tenant-Id=xyz789 exist in URL,
 * // navigates to /verify?Impr-Workflow-Id=abc123&Impr-Tenant-Id=xyz789
 * ```
 */
export const navigateWithContext = (
  navigate: NavigateFunction,
  path: string,
  options?: NavigateOptions,
): void => {
  const pathWithContext = addContextToPath(path);
  navigate(pathWithContext, options);
};

/**
 * @deprecated Use navigateWithContext instead
 */
export const navigateWithWorkflowId = navigateWithContext;

/**
 * Adds the Impr-Workflow-Id and Impr-Tenant-Id query parameters to a path if they exist in the current URL.
 * Useful for constructing URLs that need to preserve these IDs.
 *
 * @param path - The base path
 * @returns The path with Impr-Workflow-Id and Impr-Tenant-Id parameters appended if they exist
 *
 * @example
 * ```tsx
 * const link = addContextToPath('/account');
 * // If Impr-Workflow-Id=abc123 and Impr-Tenant-Id=xyz789 exist in URL,
 * // returns '/account?Impr-Workflow-Id=abc123&Impr-Tenant-Id=xyz789'
 * ```
 */
export const addContextToPath = (path: string): string => {
  const workflowId = getWorkflowIdFromUrl() || generateWorkflowId();
  
  // Get tenant ID from current URL if it exists
  const params = new URLSearchParams(window.location.search);
  const tenantId = params.get('Impr-Tenant-Id');
  
  // Handle hash fragments - insert query params before the hash
  const hashIndex = path.indexOf('#');
  const hasHash = hashIndex !== -1;
  const pathBeforeHash = hasHash ? path.substring(0, hashIndex) : path;
  const hashPart = hasHash ? path.substring(hashIndex) : '';

  const separator = pathBeforeHash.includes('?') ? '&' : '?';
  let result = `${pathBeforeHash}${separator}Impr-Workflow-Id=${encodeURIComponent(workflowId)}`;
  
  // Add tenant ID if it exists
  if (tenantId) {
    result += `&Impr-Tenant-Id=${encodeURIComponent(tenantId)}`;
  }
  
  return `${result}${hashPart}`;
};
